import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, CheckCircle2, Circle, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { FilterSelect, uniq, SearchInput, normText } from "@/components/fly/FilterSelect";
import { api, clearSession, getSession, fmt, errMsg } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { DayTabs } from "@/components/fly/DayTabs";

const DAY_KEY = "flycup_judge_day";

export default function JudgeList() {
  const nav = useNavigate();
  const session = getSession();
  const [days, setDays] = useState([]);
  const [day, setDay] = useState(localStorage.getItem(DAY_KEY) || "");
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [age, setAge] = useState("");
  const [q, setQ] = useState("");
  const [onlyTodo, setOnlyTodo] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const tick = () => api.get("/judge/stage", { params: day ? { day } : {} }).then((r) => {
      const c = r.data.current;
      if (!r.data.on_break && r.data.assigned && c && c.present && !c.scored) { nav(`/sedzia/ocena/${c.id}`, { replace: true }); return; }
      setStage(r.data);
      setReady(true);
      if (r.data.day && r.data.day !== day) setDay(r.data.day);
    }).catch(() => setReady(true));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [day, nav]);

  const loadConfig = useCallback(() => {
    api.get("/config").then((r) => {
      setDays(r.data.days);
      if (!r.data.days.includes(day)) setDay(r.data.days[0]);
    }).catch((e) => { setError(errMsg(e)); setTimeout(loadConfig, 4000); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const dayRef = useRef(day);
  const retryRef = useRef(null);
  const load = useCallback(() => {
    if (!day) return;
    clearTimeout(retryRef.current);
    api.get("/judge/performances", { params: { day } })
      .then((r) => { if (dayRef.current === day) { setItems(r.data); setError(""); } })
      .catch((e) => { if (dayRef.current === day) { setError(errMsg(e)); retryRef.current = setTimeout(load, 4000); } });
  }, [day]);

  useEffect(() => {
    if (!day) return;
    dayRef.current = day;
    localStorage.setItem(DAY_KEY, day);
    setItems([]);
    setError("");
    load();
    setCat("");
    setAge("");
    const t = setInterval(load, 30000);
    return () => { clearInterval(t); clearTimeout(retryRef.current); };
  }, [day, load]);

  const shown = useMemo(() => items.filter((p) =>
    (!onlyTodo || p.my_total == null) && (!cat || p.category === cat) && (!age || p.age_group === age) &&
    (!q.trim() || normText(p.name).includes(normText(q.trim())) || normText(p.school).includes(normText(q.trim())))
  ), [items, cat, age, onlyTodo, q]);

  const done = items.filter((p) => p.my_total != null).length;
  const logout = () => { clearSession(); nav("/sedzia/login"); };

  if (!ready) return <div data-testid="judge-stage-loading" className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <>
      <TopBar
        title={session?.name}
        right={
          <button data-testid="judge-logout-btn" onClick={logout} className="h-10 px-3 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-2 text-sm">
            <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Wyloguj</span>
          </button>
        }
      />
      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400">Sędzia {session?.judge_number}</p>
            <h1 className="font-display text-4xl font-extrabold uppercase">Lista startowa</h1>
          </div>
          <div data-testid="judge-progress" className="font-mono text-sm text-slate-300">
            Ocenione: <span className="text-emerald-400 font-bold">{done}</span> / {items.length}
          </div>
        </div>
        <div className="mt-5"><DayTabs days={days} value={day} onChange={setDay} prefix="judge-day" /></div>
        {error && <LoadError msg={error} onRetry={load} />}
        {stage && <StageCard s={stage} />}
        <div className="mt-4 flex flex-wrap gap-2">
          <SearchInput testid="judge-search-input" value={q} onChange={setQ} />
          <FilterSelect testid="judge-filter-category" className="flex-1 min-w-[200px]" value={cat} onChange={setCat} options={uniq(items.map((p) => p.category))} placeholder="Wszystkie kategorie" />
          <FilterSelect testid="judge-filter-age" className="min-w-[150px]" value={age} onChange={setAge} options={uniq(items.map((p) => p.age_group))} placeholder="Każdy wiek" />
          <button data-testid="judge-filter-todo" onClick={() => setOnlyTodo((v) => !v)}
            className={`h-11 px-4 rounded-xl text-sm font-semibold border transition-colors ${onlyTodo ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-blue-500/20 text-slate-400"}`}>
            Do oceny
          </button>
        </div>
        <ul className="mt-4 space-y-2" data-testid="judge-performance-list">
          {shown.map((p) => <PerfRow key={p.id} p={p} onOpen={() => nav(`/sedzia/ocena/${p.id}`)} />)}
          {shown.length === 0 && !error && <li className="text-slate-500 text-sm py-10 text-center">Brak występów do wyświetlenia.</li>}
        </ul>
      </main>
    </>
  );
}

export const LoadError = ({ msg, onRetry }) => (
  <div data-testid="list-load-error" className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex items-center gap-3">
    <AlertTriangle className="w-4 h-4 shrink-0" />
    <span className="flex-1">Nie udało się wczytać listy ({msg}). Ponawiam automatycznie…</span>
    <button data-testid="list-retry-btn" onClick={onRetry} className="h-9 px-3 rounded-lg border border-amber-500/40 flex items-center gap-1.5 shrink-0"><RefreshCw className="w-4 h-4" />Ponów</button>
  </div>
);

const StageCard = ({ s }) => {
  const c = s.current;
  const msg = s.on_break ? "PRZERWA – ocenianie wstrzymane przez konferansjera"
    : !c ? "Brak występów w tym dniu"
    : !s.assigned ? "Nie jesteś przypisany do oceniania w tym dniu"
    : c.scored ? "Ocena zapisana – czekaj, aż konferansjer poprowadzi kolejny występ"
    : !c.present ? "Zawodnik na scenie nie jest zarejestrowany – czekaj na rejestrację lub kolejny występ" : "Otwieram ocenę…";
  return (
    <div data-testid="judge-stage-card" className={`mt-4 rounded-2xl border p-4 sm:p-5 ${s.on_break ? "border-amber-500/60 bg-amber-500/15" : "border-blue-500/40 bg-blue-500/10"}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Teraz na scenie{s.day && ` · ${s.day}`}</div>
      {s.on_break
        ? <div className="font-display text-4xl font-black uppercase text-amber-300" data-testid="judge-stage-break">Przerwa</div>
        : c && <div className="font-display text-2xl font-bold uppercase" data-testid="judge-stage-name">{c.name}<span className="block text-sm font-sans normal-case font-normal text-slate-400">{c.category} · {c.age_group}</span></div>}
      <div className="text-sm text-slate-300 mt-1" data-testid="judge-stage-msg">{msg}</div>
    </div>
  );
};

const PerfRow = ({ p, onOpen }) => {
  const scored = p.my_total != null;
  return (
    <li>
      <button data-testid={`judge-perf-${p.id}`} onClick={onOpen}
        className="w-full text-left fly-card px-3 sm:px-4 py-3 flex items-center gap-3 sm:gap-4 transition-[border-color,transform] duration-150 hover:border-blue-500/50 active:scale-[0.995]">
        <span className="font-mono text-xs text-slate-500 w-10 sm:w-12 shrink-0">{p.time || `#${p.order}`}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold flex flex-wrap items-center gap-x-2 gap-y-1"><span className="break-words min-w-0">{p.name}</span>
            {!p.present && <span data-testid={`judge-not-registered-${p.id}`} className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase bg-red-500/15 border border-red-500/40 text-red-300"><AlertTriangle className="w-3 h-3" />niezarejestrowany</span>}
          </div>
          <div className="text-xs text-slate-400 truncate">{p.category} · {p.age_group} · {p.school}</div>
        </div>
        {scored ? (
          <span className="shrink-0 flex items-center gap-1.5 text-emerald-400 text-sm font-mono"><CheckCircle2 className="w-4 h-4" />{fmt(p.my_total)}</span>
        ) : (
          <span className="shrink-0 flex items-center gap-1.5 text-amber-400 text-xs"><Circle className="w-4 h-4" /><span className="hidden sm:inline">do oceny</span></span>
        )}
      </button>
    </li>
  );
};
